import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { readableValue } from './cycleOverviewFormat'
import MenuItemChange, { MenuWeek } from './MenuItemChange'
import NutritionTargetChange from './NutritionTargetChange'

// VH-227: before/after for every proposed operation. Operations flagged
// `requires_individual_confirmation` start unticked; nothing changes until Confirm.
export function ChangeSetPreview({ changeSet, onConfirm, onReject, busy = false, error = null, readOnly = false }) {
  const { t } = useTranslation()
  const [accepted, setAccepted] = useState(() =>
    new Set(changeSet.operations.filter((op) => !op.requires_individual_confirmation).map((op) => op.id)),
  )
  const isOpen = changeSet.status === 'proposed'

  const toggle = (id) =>
    setAccepted((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="border border-purple-200 rounded-lg p-3 bg-purple-50" data-testid={`change-set-${changeSet.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-900">{t('healthCycle.changeSet.title')}</p>
        <span className="text-xs text-gray-600">{t(`healthCycle.changeSet.status.${changeSet.status}`, changeSet.status)}</span>
      </div>
      <ul className="space-y-2">
        {changeSet.operations.map((op) => (
          <li key={op.id} className="bg-surface-container rounded-md p-2 text-sm">
            <label className="flex items-start gap-2">
              {isOpen && !readOnly && (
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={accepted.has(op.id)}
                  onChange={() => toggle(op.id)}
                  aria-label={t(`healthCycle.changeSet.ops.${op.op}`, op.op)}
                />
              )}
              <span className="min-w-0 flex-1">
                <span className="font-medium text-gray-900">{t(`healthCycle.changeSet.ops.${op.op}`, op.op)}</span>
                {op.op === 'add_menu_item' ? (
                  <MenuItemChange operation={op} />
                ) : op.op === 'set_nutrition_target' ? (
                  <>
                    <span className="text-gray-600"> — {t(`cycleReports.targetKey.${op.target?.key}`, op.target?.key ?? '')}</span>
                    <NutritionTargetChange operation={op} />
                  </>
                ) : (
                  <>
                    {op.target && <span className="text-gray-600"> — {(readableValue(op.target.name ?? op.target) ?? t('healthCycle.compare.changed'))}</span>}
                    {/* VH-236 follow-up: a menu change always names the week it lands in. */}
                    {op.effect?.week?.week_start && <MenuWeek week={op.effect.week} />}
                    <span className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1 text-xs">
                      <span className="text-gray-600">
                        {t('healthCycle.changeSet.before')}: <span className="line-through">{(readableValue(op.before) ?? t('healthCycle.compare.changed'))}</span>
                      </span>
                      <span className="text-gray-900">
                        {t('healthCycle.changeSet.after')}: {(readableValue(op.after) ?? t('healthCycle.compare.changed'))}
                      </span>
                    </span>
                  </>
                )}
                {op.requires_individual_confirmation && (
                  <span className="block text-xs text-yellow-800 mt-1">{t('healthCycle.changeSet.individual')}</span>
                )}
                {op.affected_sections?.length > 0 && (
                  <span className="block text-xs text-gray-500 mt-1">
                    {t('healthCycle.changeSet.affects', {
                      sections: op.affected_sections.map((s) => t(`healthCycle.sections.${s}`, s)).join(', '),
                    })}
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {changeSet.affected_steps?.length > 0 && (
        <p className="text-xs text-gray-600 mt-2">
          {t('healthCycle.changeSet.recomputed')}
        </p>
      )}
      {error && <p className="text-xs text-red-600 mt-2" role="alert">{error}</p>}
      {isOpen && !readOnly && (
        <div className="flex justify-end gap-2 mt-3">
          <button type="button" className="btn-outline text-sm" disabled={busy} onClick={() => onReject(changeSet)}>
            {t('healthCycle.changeSet.reject')}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={busy || accepted.size === 0}
            onClick={() => onConfirm(changeSet, [...accepted])}
          >
            {t('healthCycle.changeSet.confirm', { count: accepted.size })}
          </button>
        </div>
      )}
    </div>
  )
}

export default ChangeSetPreview
